import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BackLink } from "../ui/BackLink";
import { useMarkableEvents, usePeopleSearch } from "../data";
import { presenceRoster, recordPresence, type RosterPerson } from "../actions";
import { NOUNS, count, formatDate } from "../hebrew";
import { IcCalendar, IcCheck, IcPeople, IcPlus, IcSearch } from "../ui/icons";

/**
 * Roll Call — "מי היה".
 *
 * The attendance control on an event only worked on people who registered, which
 * makes it useless for the things that actually happen most: a minyan, a class, a
 * Shabbat table. This is spec §4.6, and its budget is explicit — a typical group
 * marked in under thirty seconds. That rules out a dialog per person, so marking
 * is one tappable grid with a running count and a single save.
 *
 * ## It hangs off an event, not off a list of gathering kinds
 *
 * The first version of this screen asked "shacharit or mincha or a class?" from a
 * fixed list, with a row of tabs. The product owner rejected it:
 *
 *   > הסימון נוכחות צריך לאפשר לסמן רק אירועים שאני יצרתי ולא אירועים קשיחים
 *   > כמו שחרית מנחה וכל הרשימה שיצרת לה טאבים. שזה דרך ממש לא נוחה.
 *
 * He was right twice over. The tabs were slow, and a hardcoded vocabulary is a
 * second calendar competing with the real one — the CRM would hold opinions about
 * which gatherings exist while the events collection holds the actual ones. A
 * daily minyan he wants tracked is a recurring event, which the system now has.
 *
 * So the screen opens on his own events, most recent first, and marking is
 * attached to the one he picks.
 *
 * ## The roster is learned, not guessed
 *
 * Registered people first — they said they were coming. Then the regulars of this
 * series: the people actually seen at earlier occurrences of this class or this
 * Shabbat, most frequent first. With 3,893 people and no history, any ordering
 * the system invented would be arbitrary, and an arbitrary grid of fifty names is
 * slower to use than a search box. The screen gets faster the more it is used
 * instead of needing to be right on day one.
 *
 * ## Coverage is a required answer, not a detail
 *
 * §4.6: *הנוכחות היא observation, לא הוכחה שמי שלא סומן נעדר.* An unticked name
 * means nothing on its own — the list may have been abandoned halfway. So the
 * screen asks whether everybody was marked, and only "everybody" may ever support
 * an inference about who stopped coming.
 */

function timeOf(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Jerusalem",
  }).format(d);
}

export function RollCallPage() {
  const [params, setParams] = useSearchParams();
  const eventId = params.get("event");

  return eventId ? (
    <MarkEvent eventId={eventId} onBack={() => setParams({}, { replace: true })} />
  ) : (
    <PickEvent />
  );
}

/** Which event. His own events, most recent first — nothing invented. */
function PickEvent() {
  const events = useMarkableEvents();
  const [, setParams] = useSearchParams();

  const rows = useMemo(
    () => events.data.filter((e) => e.status !== "cancelled"),
    [events.data],
  );

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>מי היה</h1>
            <p className="sub">בחרו אירוע וסמנו מי הגיע</p>
          </div>
        </div>
      </header>

      <div className="body">
        <BackLink fallback="/more" label="חזרה" />

        {events.error && <div className="notice error">{events.error}</div>}

        {events.loading ? (
          <div className="skeleton" style={{ height: 160 }} />
        ) : rows.length === 0 ? (
          <div className="empty">
            <div
              className="empty-ico tint-green"
            >
              <IcCalendar size={26} />
            </div>
            <h3>אין אירועים לסימון</h3>
            <p>
              מסמנים נוכחות באירוע שיצרתם. אירוע קבוע, כמו שיעור, מניין או שבת, יוצרים פעם אחת
              כאירוע חוזר, וכל מפגש שלו יופיע כאן ביום שלו.
            </p>
            <Link className="btn btn-primary" to="/events" style={{ marginTop: 12 }}>
              לאירועים
            </Link>
          </div>
        ) : (
          <div className="card">
            <div className="card-title">אירועים שהיו</div>
            {rows.map((e) => {
              const iso = e.startAt?.toDate?.().toISOString() ?? null;
              return (
                <button
                  key={e.id}
                  className="list-row"
                  onClick={() => setParams({ event: e.id })}
                  style={{ width: "100%", textAlign: "start" }}
                >
                  <span className="grow">
                    <b>{e.title}</b>
                    <span className="faint" style={{ display: "block", marginTop: 2 }}>
                      {formatDate(iso)} · {timeOf(iso)}
                      {e.seriesId ? " · מתוך סדרה" : ""}
                    </span>
                  </span>
                  <IcPeople size={16} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MarkEvent({ eventId, onBack }: { eventId: string; onBack: () => void }) {
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState<{ iso: string; hebrew: string | null } | null>(null);
  const [rosterSource, setRosterSource] = useState<"registered" | "series" | "none">("none");
  const [observations, setObservations] = useState(0);

  const [roster, setRoster] = useState<RosterPerson[]>([]);
  const [extra, setExtra] = useState<RosterPerson[]>([]);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [coverage, setCoverage] = useState<"complete" | "partial">("partial");
  const [unknownCount, setUnknownCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [term, setTerm] = useState("");

  const search = usePeopleSearch(term);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setSaved(null);
    presenceRoster(eventId)
      .then((r) => {
        if (cancelled) return;
        setTitle(r.event.title);
        setWhen({ iso: r.event.startIso, hebrew: r.event.hebrewLabel });
        setRosterSource(r.rosterSource);
        setObservations(r.observations);
        setRoster(r.roster);
        setExtra([]);
        setTicked(new Set(r.today?.participantIds ?? []));
        setCoverage(r.today?.coverage === "complete" ? "complete" : "partial");
        setUnknownCount(r.today?.unknownCount ?? 0);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : "לא הצלחנו לטעון את הרשימה. נסו שוב.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const grid = useMemo(() => {
    const seen = new Set(roster.map((p) => p.id));
    return [...roster, ...extra.filter((p) => !seen.has(p.id))];
  }, [roster, extra]);

  function toggle(id: string) {
    setSaved(null);
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function add(person: { id: string; displayName?: string }) {
    setExtra((prev) =>
      prev.some((p) => p.id === person.id)
        ? prev
        : [
            ...prev,
            {
              id: person.id,
              name: person.displayName ?? "",
              times: 0,
              lastIso: "",
              isChild: false,
              registered: false,
            },
          ],
    );
    setTicked((prev) => new Set(prev).add(person.id));
    setTerm("");
    setSaved(null);
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const r = await recordPresence({
        eventId,
        participantIds: [...ticked],
        coverage,
        unknownCount,
      });
      setSaved(
        r.isUpdate
          ? `עדכנו. ${count(r.present, NOUNS.present)}${r.removed > 0 ? `, ביטלנו ${r.removed} סימונים` : ""}.`
          : `שמרנו. ${count(r.present, NOUNS.present)}.`,
      );
      // Reload, so the roster reflects what was just recorded.
      const fresh = await presenceRoster(eventId);
      setRoster(fresh.roster);
      setExtra([]);
      setObservations(fresh.observations);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  const nothingMarked = ticked.size === 0 && unknownCount === 0;

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>{loading ? "מי היה" : title}</h1>
            <p className="sub">
              {when ? `${formatDate(when.iso)} · ${timeOf(when.iso)}` : "סימון נוכחות"}
              {when?.hebrew ? ` · ${when.hebrew}` : ""}
            </p>
          </div>
        </div>
      </header>

      <div className="body body-clear-bar">
        <button className="faint" onClick={onBack} style={{ padding: "6px 0" }}>
          ← אירוע אחר
        </button>

        {err && <div className="notice error">{err}</div>}
        {saved && <div className="notice info">{saved}</div>}

        {loading ? (
          <div className="skeleton" style={{ height: 160 }} />
        ) : (
          <>
            {grid.length === 0 ? (
              <div className="empty">
                <div
                  className="empty-ico tint-green"
                >
                  <IcPeople size={26} />
                </div>
                <h3>אין עוד רשימה לאירוע הזה</h3>
                <p>
                  חפשו את מי שהגיע והוסיפו אותו. בפעם הבאה, מי שסימנתם יופיע למעלה, לפי מי
                  שמגיע הכי הרבה. בפעם השלישית זה כבר שתי לחיצות.
                </p>
              </div>
            ) : (
              <>
                {/* Why the list looks the way it does. An unexplained grid of
                    names reads as arbitrary even when it is not. */}
                <p className="faint" style={{ margin: "4px 0 8px" }}>
                  {rosterSource === "registered"
                    ? "הנרשמים לאירוע מופיעים למעלה."
                    : rosterSource === "series"
                      ? `לפי מי שהגיע ל${observations > 1 ? `${observations} המפגשים הקודמים` : "המפגש הקודם"} בסדרה.`
                      : "זו רשימה ראשונה. הוסיפו את מי שהגיע, והיא תיבנה מעצמה."}
                </p>
                <div className="rollcall-grid">
                  {grid.map((p) => (
                    <button
                      key={p.id}
                      className={ticked.has(p.id) ? "rc-chip on" : "rc-chip"}
                      onClick={() => toggle(p.id)}
                      disabled={busy}
                      aria-pressed={ticked.has(p.id)}
                    >
                      {ticked.has(p.id) && <IcCheck size={13} />}
                      {p.name || "בלי שם"}
                      {/* Regularity, so the grid reads as a roster rather than an
                          arbitrary pile of names. A registrant gets a dot
                          instead — they have no history here yet. */}
                      {p.registered ? (
                        <span className="rc-times">נרשם</span>
                      ) : (
                        p.times > 1 && <span className="rc-times">{p.times}</span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}

            {adding ? (
              <div className="card" style={{ padding: 12, marginTop: 12 }}>
                <div className="searchbar" style={{ marginTop: 0 }}>
                  <IcSearch size={18} />
                  <input
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="שם של מי שהגיע…"
                    autoFocus
                    autoComplete="off"
                  />
                </div>
                {term.trim().length >= 2 && (
                  <div style={{ marginTop: 8 }}>
                    {search.data.length === 0 ? (
                      <p className="faint">לא מצאנו. אפשר לספור אותו למטה, עם מי שלא במערכת.</p>
                    ) : (
                      search.data.slice(0, 8).map((p) => (
                        <button
                          key={p.id}
                          className="list-row"
                          onClick={() => add(p)}
                          style={{ width: "100%", textAlign: "start" }}
                        >
                          <span className="grow">{p.displayName ?? "בלי שם"}</span>
                          <IcPlus size={15} />
                        </button>
                      ))
                    )}
                  </div>
                )}
                <div className="btn-row" style={{ marginTop: 10 }}>
                  <button
                    className="btn"
                    onClick={() => {
                      setAdding(false);
                      setTerm("");
                    }}
                  >
                    סיום
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="btn btn-wide"
                onClick={() => setAdding(true)}
                style={{ marginTop: 12 }}
              >
                <IcPlus size={16} /> הוספת מי שלא ברשימה
              </button>
            )}

            <div className="card" style={{ marginTop: 14 }}>
              <div className="card-title">הגיעו עוד אנשים שלא במערכת</div>
              <div
                style={{
                  padding: "0 14px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <button
                  className="btn"
                  onClick={() => setUnknownCount((n) => Math.max(0, n - 1))}
                  disabled={busy || unknownCount === 0}
                  aria-label="פחות"
                >
                  −
                </button>
                <b className="t-2xl" style={{ minWidth: 28, textAlign: "center" }}>{unknownCount}</b>
                <button
                  className="btn"
                  onClick={() => setUnknownCount((n) => n + 1)}
                  disabled={busy}
                  aria-label="עוד"
                >
                  +
                </button>
                <span className="faint" style={{ marginTop: 0 }}>
                  נספרים, אבל לא נשמרים בשמות
                </span>
              </div>
            </div>

            {/* The answer that decides what this record may later be used for. */}
            <div className="card">
              <div className="card-title">מה סומן</div>
              <div className="card-pad">
                <div className="chips">
                  <button
                    className={coverage === "complete" ? "tagchip tagchip-on" : "tagchip"}
                    onClick={() => setCoverage("complete")}
                    disabled={busy}
                  >
                    סימנתי את כולם
                  </button>
                  <button
                    className={coverage === "partial" ? "tagchip tagchip-on" : "tagchip"}
                    onClick={() => setCoverage("partial")}
                    disabled={busy}
                  >
                    סימנתי חלק
                  </button>
                </div>
                <p className="faint" style={{ marginTop: 8 }}>
                  {coverage === "complete"
                    ? "ככה נוכל לדעת גם מי הפסיק להגיע."
                    : "מי שלא סימנתם לא ייחשב נעדר. מסימון חלקי לא נסיק כלום על אף אחד."}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {!loading && (
        <div className="selbar">
          <span className="grow">
            {nothingMarked ? "עוד לא סימנתם אף אחד" : count(ticked.size, NOUNS.present)}
            {unknownCount > 0 && ` + ${unknownCount}`}
          </span>
          <button className="btn btn-primary" onClick={save} disabled={busy || nothingMarked}>
            {busy ? "שומרים…" : "שמרו"}
          </button>
        </div>
      )}
    </div>
  );
}
